import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import { PencilSimpleIcon } from '@phosphor-icons/react';

import { useApplyManualEdits } from '../../api/tenant-profile-template-api';
import type { ActiveTemplateCategory } from '../../api/types';
import {
  buildCategoryDefaults,
  buildCategorySchema,
  type FieldFormValues,
} from '../utils/categorySchema';
import { normalizeTableCells, stripEmptyTableRows } from '../utils/fieldValue';
import { bannerGradientSx, ORG_TOKENS } from '../utils/tokens';

import { FieldRenderer } from './fields/FieldRenderer';
import { SectionLabel } from './SectionLabel';

interface Props {
  category: ActiveTemplateCategory;
  categoryName: string;
}

const isWide = (type: string) => type === 'table' || type === 'textarea';

function isConflict(error: unknown): boolean {
  return (
    (error as { response?: { status?: number } } | null)?.response?.status ===
    409
  );
}

/**
 * Editable form for a single category, powered by react-hook-form with a
 * per-category Zod schema derived from each field's type/validation/required
 * rules. Only the fields the user actually changed are sent to
 * `useApplyManualEdits` (which mints a new version and is the implicit
 * "approval"). Surfaces inline per-field validation plus the validated /
 * saving / locked (409) banners from the design.
 *
 * Opens in read-only mode (values shown as text) with an "Edit fields" button;
 * clicking it reveals the editable inputs with Save / Cancel.
 */
export const CategoryEditForm: FC<Props> = observer(
  ({ category, categoryName }) => {
    const { t } = useTrans();
    const applyEdits = useApplyManualEdits();
    const [editing, setEditing] = useState(false);

    const schema = useMemo(
      () => buildCategorySchema(category.fields),
      [category.fields],
    );
    const defaultValues = useMemo(
      () => buildCategoryDefaults(category.fields),
      [category.fields],
    );
    const fieldTypeById = useMemo(
      () => new Map(category.fields.map((f) => [f.id, f.type])),
      [category.fields],
    );
    const tableColumnsById = useMemo(
      () =>
        new Map(
          category.fields
            .filter((f) => f.type === 'table')
            .map((f) => [f.id, f.columns ?? []]),
        ),
      [category.fields],
    );

    const {
      control,
      handleSubmit,
      reset,
      formState: { dirtyFields, isDirty },
    } = useForm<FieldFormValues>({
      resolver: zodResolver(schema),
      defaultValues,
      mode: 'onChange',
    });

    const saving = applyEdits.isPending;
    const locked = isConflict(applyEdits.error);

    // Keep a ref so the effect below can read the latest editing state without
    // it becoming a dependency (we only want to re-sync on server data changes).
    const editingRef = useRef(editing);
    editingRef.current = editing;

    // Re-sync form defaults whenever fresh server data arrives (e.g. after save
    // invalidates the template query). Gated on !editing so in-progress edits
    // are never discarded by a background poll.
    useEffect(() => {
      if (!editingRef.current) {
        reset(defaultValues);
      }
    }, [defaultValues, reset]);

    // Translate our own message keys — the schema emits one where it has
    // something better to say than zod's default. Anything else is a zod
    // built-in (English) message and passes through unchanged.
    const errorText = (message?: string): string | undefined => {
      switch (message) {
        case 'app.orgProfile.field.requiredError':
          return t('app.orgProfile.field.requiredError');
        case 'app.orgProfile.field.phoneError':
          return t('app.orgProfile.field.phoneError');
        default:
          return message;
      }
    };

    const handleCancel = () => {
      reset(defaultValues);
      applyEdits.reset();
      setEditing(false);
    };

    const onValid = (values: FieldFormValues) => {
      // Normalize before saving: drop fully-empty table rows so an added-but-
      // never-filled row is treated as non-existent (and never trips the
      // backend's required-column check).
      const normalized: FieldFormValues = { ...values };
      const changed: Record<string, unknown> = {};
      for (const id of Object.keys(dirtyFields)) {
        if (fieldTypeById.get(id) === 'table') {
          // Drop empty rows, then coerce each cell to its column's declared type
          // so a list column edited as a comma string (or any wrong-shaped cell)
          // is saved as the array/type the backend requires, not a bare string.
          const columns = tableColumnsById.get(id) ?? [];
          const stripped = normalizeTableCells(
            stripEmptyTableRows(values[id]),
            columns,
          );
          normalized[id] = stripped;
          // Adding then leaving a row empty leaves the value unchanged — only
          // send it when it actually differs from what was loaded.
          if (JSON.stringify(stripped) !== JSON.stringify(defaultValues[id])) {
            changed[id] = stripped;
          }
        } else {
          changed[id] = values[id];
        }
      }

      if (Object.keys(changed).length === 0) {
        // Nothing real changed (e.g. only an empty row); clear it from the form.
        reset(normalized);
        setEditing(false);
        return;
      }

      applyEdits.mutate(changed, {
        onSuccess: () => {
          reset(normalized);
          setEditing(false);
        },
      });
    };

    return (
      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit(onValid)}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Box
          sx={(theme) => ({
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
            p: 1.75,
            borderRadius: `${ORG_TOKENS.radiusMd}px`,
            ...bannerGradientSx(theme, !editing ? 'green' : saving ? 'grey' : 'blue'),
          })}
        >
          <Typography variant="textSM_regular" color="text.secondary">
            {!editing
              ? t('app.orgProfile.panel.readOnly', { category: categoryName })
              : saving
                ? t('app.orgProfile.banner.savingLocked', { category: categoryName })
                : t('app.orgProfile.banner.editing', { category: categoryName })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
            {!editing ? (
              <Button
                type="button"
                variant="tertiary"
                startIcon={<PencilSimpleIcon size={16} />}
                onClick={() => setEditing(true)}
                sx={{ textTransform: 'none' }}
              >
                {t('app.orgProfile.panel.editFields')}
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="tertiary"
                  onClick={handleCancel}
                  disabled={saving}
                  sx={{ textTransform: 'none' }}
                >
                  {t('app.orgProfile.panel.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving || !isDirty}
                  sx={{
                    textTransform: 'none',
                    backgroundColor: 'green.700',
                    color: 'common.white',
                    '&:hover': { backgroundColor: 'green.800' },
                  }}
                >
                  {saving
                    ? t('app.orgProfile.panel.saving')
                    : t('app.orgProfile.panel.saveChanges')}
                </Button>
              </>
            )}
          </Box>
        </Box>

        {applyEdits.isError && (
          <Typography variant="textSM_regular" color="yellow.800">
            {locked
              ? t('app.orgProfile.banner.locked')
              : t('app.orgProfile.banner.saveFailed')}
          </Typography>
        )}

        <SectionLabel>
          {editing
            ? t('app.orgProfile.panel.editableValues')
            : t('app.orgProfile.panel.approvedValues')}
        </SectionLabel>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            columnGap: 2,
            rowGap: 2,
          }}
        >
          {category.fields.map((field) => (
            <Controller
              key={field.id}
              name={field.id}
              control={control}
              render={({ field: rhf, fieldState }) => (
                <Box sx={{ gridColumn: isWide(field.type) ? '1 / -1' : 'auto', minWidth: 0, overflow: 'hidden' }}>
                  <FieldRenderer
                    field={field}
                    mode={editing ? 'edit' : 'read'}
                    value={rhf.value}
                    dirty={fieldState.isDirty}
                    disabled={saving}
                    error={errorText(fieldState.error?.message)}
                    onChange={rhf.onChange}
                  />
                </Box>
              )}
            />
          ))}
        </Box>
      </Box>
    );
  },
);
